/**
 * Phase 1.7 — right-hand rail.
 *
 * Per-page opt-in. The dashboard renders one with weather + tasks; detail
 * pages render one with linked records + quick stats; card-grid pages do
 * NOT render one and use the full content width instead.
 *
 * The rail itself is a thin wrapper — content is composed from `<Card>`
 * children. We provide a `RailSection` helper for the typical "title +
 * body + see-all link" pattern.
 *
 * Width is fixed at 320px; mobile renders the rail as a stacked column
 * BELOW the main content rather than alongside.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface RightRailProps {
  children: ReactNode;
  className?: string;
}

export function RightRail({ children, className }: RightRailProps): JSX.Element {
  return (
    <aside
      className={cn(
        'shrink-0 space-y-4 lg:w-rail-w',
        // On mobile, sits below main content; on lg+ pinned to the right.
        className,
      )}
      aria-label="Page rail"
    >
      {children}
    </aside>
  );
}

export interface RailSectionProps {
  title: string;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function RailSection({ title, children, action, className }: RailSectionProps): JSX.Element {
  return (
    <section
      className={cn(
        'rounded-xl border border-border bg-surface p-4',
        'dark:bg-surface-dark dark:border-border',
        className,
      )}
    >
      <header className="mb-3 flex items-center justify-between">
        <h3 className="font-mono text-2xs uppercase tracking-wider text-muted-foreground">{title}</h3>
        {action}
      </header>
      {children}
    </section>
  );
}
